import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import { Dayjs } from "dayjs"
import styled from "styled-components"

import {
  useLazyGetDevicesMessagesQuery,
  useLazyGetMessageTemplatesQuery,
  useScheduleMessageForDevicesMutation,
} from "store/api/contractorsApi"
import { useAppSelector } from "store/hooks"
import { ApiTypes, UITypes } from "types"
import SubscriptionPrivilege from "types/enums/SubscriptionPrivilege"
import { ReactComponent as IconDelete } from "assets/svg/icon-delete.svg"
import { ReactComponent as IconMessages } from "assets/svg/menu-messages.svg"

import getErrorMessage from "utils/getErrorMessage"
import hasSubscriptionAccess from "utils/hasSubscriptionAccess"
import { RemoveScheduledMessageModal } from "modals/RemoveScheduledMessageModal"
import { SubscriptionUpgradeModal } from "modals/SubscriptionUpgradeModal/SubscriptionUpgradeModal"
import { EmptyState } from "components/common/EmptyState"

import { useCustomerDashboard } from "../CustomerDashboardContext"
import { StyledTableWrapper } from "../styled"
import { CustomerDashboardMessagesHeader } from "./CustomerDashboardMessagesHeader"
import { customerDashboardMessagesHeaderCells } from "./customerDashboardMessagesHeaderCells"
import { CustomerDashboardMessagesHistory } from "./CustomerDashboardMessagesHistory"

const MessagesContainer = styled.div<{ sidebarOpen?: boolean }>`
  display: flex;
  flex-direction: column;
  margin-top: 2px;
  padding: 0 ${({ sidebarOpen }) => (sidebarOpen ? "24px" : "0")} 20px 0;
  height: calc(100dvh - 255px);
  overflow-y: auto;
`

export const CustomerDashboardMessages = () => {
  const { devices, currentDevice, isSidebarOpen } = useCustomerDashboard()
  const selectedDevice = devices[currentDevice]
  const userProfile = useAppSelector(state => state.user.userProfile)
  const hasMessagesAccess = userProfile
    ? hasSubscriptionAccess(userProfile, SubscriptionPrivilege.MessageCenter)
    : false

  const [templateID, setTemplateID] = useState<UITypes.Option[] | undefined>(undefined)
  const [toBeRemovedMessage, setToBeRemovedMessage] = useState<ApiTypes.Model.DeviceMessage | null>(null)
  const [view, setView] = useState<"list" | "history">("list")

  const [triggerGetDevicesMessages, { data: messagesData, isFetching }] = useLazyGetDevicesMessagesQuery()
  const [triggerGetTemplates, { data: templatesData }] = useLazyGetMessageTemplatesQuery()
  const [triggerScheduleMessage, { isLoading: isSending }] = useScheduleMessageForDevicesMutation()

  useEffect(() => {
    if (!selectedDevice?.id || !hasMessagesAccess) return
    triggerGetDevicesMessages([selectedDevice.id])
    triggerGetTemplates()
  }, [selectedDevice?.id, hasMessagesAccess, triggerGetDevicesMessages, triggerGetTemplates])

  const templateTitleById = useMemo(() => {
    const map = new Map<number, string>()
    templatesData?.data?.forEach(template => map.set(template.id, template.title))
    return map
  }, [templatesData])

  const deviceMessages = useMemo(
    () => messagesData?.data?.find(item => item.device_id === selectedDevice?.id)?.messages ?? [],
    [messagesData, selectedDevice?.id],
  )

  const tableData = useMemo<UITypes.TableBodyRow[]>(
    () =>
      deviceMessages.map(
        message =>
          ({
            schedule: { value: message.scheduled_at },
            message_title: { value: templateTitleById.get(message.message_template_id) ?? "" },
            rowSettings: { id: message.message_id, item: message },
          }) as UITypes.TableBodyRow,
      ),
    [deviceMessages, templateTitleById],
  )

  const handleSendMessage = async () => {
    if (!templateID?.length || !selectedDevice) return

    const res = await triggerScheduleMessage({
      device_ids: [selectedDevice.id],
      message_template_id: templateID[0].value as number,
      scheduled_delivery_timestamp: "immediately",
    })

    const error = getErrorMessage("error" in res ? res.error : undefined)
    if (error) {
      toast.error(error)
      return
    }

    toast.success(
      "The message will be sent to the customer during 1 hour. Navigate to the History page to check the delivery status.",
    )
    setTemplateID(undefined)
  }

  const handleSendScheduledMessage = async (dateTime: Dayjs) => {
    if (!templateID?.length || !selectedDevice) return

    const res = await triggerScheduleMessage({
      device_ids: [selectedDevice.id],
      message_template_id: templateID[0].value as number,
      scheduled_delivery_timestamp: dateTime.toISOString(),
    })

    const error = getErrorMessage("error" in res ? res.error : undefined)
    if (error) {
      toast.error(error)
      return
    }

    toast.success(`Message scheduled for ${dateTime.format("MMMM DD, YYYY hh:mm A")}.`)
    setTemplateID(undefined)
  }

  const getRowActions = (rowData: UITypes.TableBodyRow) => ({
    className: "",
    actionButtons: [
      {
        icon: <IconDelete />,
        tooltip: "Cancel scheduled message",
        tooltipPosition: "top-start" as const,
        handler: () => setToBeRemovedMessage(rowData.rowSettings?.item as ApiTypes.Model.DeviceMessage),
      },
    ],
  })

  const showEmptyState = hasMessagesAccess && !isFetching && tableData.length === 0

  if (!selectedDevice) return null

  return (
    <>
      <MessagesContainer sidebarOpen={isSidebarOpen}>
        {view === "history" ? (
          <CustomerDashboardMessagesHistory onBack={() => setView("list")} />
        ) : (
          <>
            <CustomerDashboardMessagesHeader
              templateID={templateID}
              setTemplateID={setTemplateID}
              onSendMessage={handleSendMessage}
              onSendScheduledMessage={handleSendScheduledMessage}
              isSending={isSending}
              onHistoryClick={() => setView("history")}
            />
            {showEmptyState ? (
              <EmptyState
                icon={<IconMessages fill="currentColor" />}
                title="No scheduled messages"
                description="There are no scheduled messages for this device yet."
              />
            ) : (
              <StyledTableWrapper
                headerCells={customerDashboardMessagesHeaderCells}
                columnsLoaded={true}
                data={tableData}
                isLoading={isFetching && hasMessagesAccess}
                getRowActions={getRowActions}
                fitContainer={true}
              />
            )}
          </>
        )}
      </MessagesContainer>
      {!hasMessagesAccess && <SubscriptionUpgradeModal page={SubscriptionPrivilege.MessageCenter} />}
      {toBeRemovedMessage && (
        <RemoveScheduledMessageModal
          deviceMessage={toBeRemovedMessage}
          onCancel={() => setToBeRemovedMessage(null)}
          onSuccess={() => setToBeRemovedMessage(null)}
        />
      )}
    </>
  )
}
